import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/LabReportsEntry.css";
import "../../../center admin css/centerAdmin.css";

interface BnuActivePatientCardProps {
  patient?: {
    id?: string;
    emrId?: string;
    name?: string;
    genderShort?: "M" | "F";
    ageYears?: number;
    avatarUrl?: string;
    sessionId?: string;
  };
}

const BnuActivePatientCard: React.FC<BnuActivePatientCardProps> = ({
  patient,
}) => {
  const [displayUrl, setDisplayUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      // Always clear previous patient's image first.
      setDisplayUrl(undefined);

      // No active B&U patient = no image.
      if (!patient) {
        return;
      }

      const profileKey = patient.avatarUrl || "";

      // Patient exists but there is no photo key.
      if (!profileKey) {
        return;
      }

      try {
        const token = sessionStorage.getItem("accessToken");

        if (!token) {
          console.warn("No access token found");
          return;
        }

        // If the queue already supplied a presigned URL,
        // use it directly.
        if (
          profileKey.includes("X-Amz-Signature") ||
          profileKey.includes("X-Amz-Credential")
        ) {
          setDisplayUrl(profileKey);
          return;
        }

        // Remove leading slash if present.
        const cleanPath = profileKey.startsWith("/")
          ? profileKey.slice(1)
          : profileKey;

        const parts = cleanPath.split("/");

        // Expected structure:
        // checkin/capture-photo/P-2026-0000126-5/photo.jpg

        if (parts.length < 3) {
          console.warn("Invalid B&U profile key:", profileKey);
          return;
        }

        const module = `${parts[0]}/${parts[1]}`;
        const key = parts.slice(2).join("/");

        const response = await api.S3config.get("/view-url", {
          params: {
            module,
            key,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const signedUrl =
          response.data?.url || (response as any).url;

        if (signedUrl) {
          setDisplayUrl(signedUrl);
        }
      } catch (err) {
        console.error(
          "Failed to fetch presigned URL for BnuActivePatientCard:",
          err,
        );
      }
    };

    fetchSignedUrl();
  }, [patient?.avatarUrl, patient?.emrId]);

  const genderText =
    patient?.genderShort === "M"
      ? "Male"
      : patient?.genderShort === "F"
        ? "Female"
        : "-";

  const ageText =
    typeof patient?.ageYears === "number"
      ? `${patient.ageYears} Years`
      : "-";

  // Important:
  // Do not render anything when B&U has no active patient.
  if (!patient) {
    return null;
  }

  return (
    <div className="patient-details-card">
      <div className="patient-details-top">
        <div className="patient-avatar-large">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Patient"
              className="patient-avatar-img"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="patient-avatar-fallback" />
          )}
        </div>

        <div className="patient-info-group">
          <div className="patient-info-row">
            <div>
              <span className="field-label">EMR ID</span>
              <div className="field-value">
                {patient.emrId ?? "-"}
              </div>
            </div>

            <div>
              <span className="field-label">Name</span>
              <div className="field-value">
                {patient.name ?? "-"}
              </div>
            </div>
          </div>

          <div className="patient-info-row">
            <div>
              <span className="field-label">Age</span>
              <div className="field-value">
                {ageText}
              </div>
            </div>

            <div>
              <span className="field-label">Gender</span>
              <div className="field-value">
                {genderText}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BnuActivePatientCard;